import Head from 'next/head';
import { useRouter } from 'next/router';
import { useContext, useEffect, useState } from 'react';
import { AppBar, Container, Tab, Tabs, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import mixpanel from 'mixpanel-browser';
import Markdown from 'react-markdown';
import rehypeExternalLinks from 'rehype-external-links';

// Server-side only imports (Nextjs will automatically remove these in the client bundle)
import fs from 'fs';
// End server-side imports

import PackageIcon from '@material-ui/icons/WidgetsSharp';
import CreditIcon from '@material-ui/icons/CameraSharp';
import ChartIcon from '@material-ui/icons/AspectRatioSharp';

import { logAuthenticationRequired } from '../utils/authLib';
import { AppContext } from '../components/AppState';
import { cleanUserId } from '../utils/formatAppLib';
import TabPanel from '../components/TabPanel';
import { useStripeClientSecret } from '../utils/users';
import theme from '../utils/theme';
import { useAuth } from '../utils/useAuth';

const ProductTypes = Object.freeze({
  PACKAGES: 0,
  CREDITS: 1,
  FRAMES: 2,
});

const pricingTableIds = (process.env.NEXT_PUBLIC_STAGE_RESOURCE === 'prod') ? {
  [ProductTypes.PACKAGES]: 'prctbl_1PTJuFGuk7YU2S0SoaTne296',
  [ProductTypes.CREDITS]: 'prctbl_1PTK0SGuk7YU2S0SyoOzyQPC',
  [ProductTypes.FRAMES]: 'prctbl_1PTK45Guk7YU2S0SJqABBSYf',
} : {
  [ProductTypes.PACKAGES]: 'prctbl_1PJZXNGuk7YU2S0SY21pM5d6',
  [ProductTypes.CREDITS]: 'prctbl_1PLGmJGuk7YU2S0SR8pjUlnJ',
  [ProductTypes.FRAMES]: 'prctbl_1PLH7yGuk7YU2S0S6Nuq4dNQ',
};

export async function getStaticProps() {
  return {
    props: {
      pricingTableFaqs: {
        [ProductTypes.PACKAGES]: fs.readFileSync('markdown/buy-packages.md', 'utf8'),
        [ProductTypes.CREDITS]: fs.readFileSync('markdown/buy-credits.md', 'utf8'),
        [ProductTypes.FRAMES]: fs.readFileSync('markdown/buy-frames.md', 'utf8'),
      },
    },
  };
}

const useStyles = makeStyles((theme) => ({
  tabs: {
    backgroundColor: theme.palette.accent.main,
    boxShadow: 'none',
    border: '1px solid ' + theme.palette.secondary.light,
  },
  tab: {
    height: '48px',
    minWidth: 100,
  },
  indicator: {
    top: '0px',
    height: '100%',
    backgroundColor: 'grey',
    opacity: 0.1,
  },
  image: {
    width: '80%',
    display: 'block',
    margin: '0 auto', // Add this line to center the image horizontally
  },
}));

function FaqImage(props) {
  const classes = useStyles();
  const { node, ...rest } = props;
  return <img className={classes.image} {...rest}/>
}

function Buy({ pricingTableFaqs }) {
  const router = useRouter();
  const { isAuthenticated, user } = useAuth();

  const { clientSecret } = useStripeClientSecret();

  const classes = useStyles();

  const { galleryUser, isAccountLoaded } = useContext(AppContext);
  const [productType, setProductType] = useState(ProductTypes.PACKAGES);
  const [isQueryLoaded, setIsQueryLoaded] = useState(false);
  const [redirect, setRedirect] = useState(null);

  const isSelfUser = user && (user.sub === galleryUser);
  const isSelfUserReady = user && galleryUser;

  useEffect(() => {
    mixpanel.track('View Buy');
    document.body.style.backgroundColor = theme.palette.accent.main;

    return () => {
      // FIXME: This is a hack to reset the background color. We should at least make sure that the background
      // from the global theme is the same colour.
      document.body.style.backgroundColor = theme.palette.primary.background;
    }
  }, []);

  useEffect(() => {
    // Handle direct product type URL, if set
    if (!router.isReady) return;
    if (!isAuthenticated) return;
    if (!isAccountLoaded) return;
    // if (isQueryLoaded) return;
    setIsQueryLoaded(true);
    const productTypeQuery = router.query['p'];
    if (productTypeQuery) {
      const productTypeIndex = ProductTypes[productTypeQuery.toUpperCase()];
      if (productTypeIndex !== undefined) {
        setProductType(productTypeIndex);
      }
    }
  }, [isAuthenticated, router.isReady, isAccountLoaded, router.query, isQueryLoaded]);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3/pricing-table.js';
    script.async = true;

    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    }
  }, []);

  async function updateProductType(productType) {
    setProductType(productType);
    if (router.isReady) {
      let newUrlParams = new URLSearchParams(window.location.search);
      const oldQueryString = newUrlParams.toString();
      const newProductQuery = Object.keys(ProductTypes).find(key => ProductTypes[key] === productType).toLowerCase();
      newUrlParams.set('p', newProductQuery);
      const newQueryString = newUrlParams.toString();
      if (newQueryString !== oldQueryString) {
        if (newQueryString.length > 0) {
          router.replace(window.location.pathname + '?' + newQueryString);
        } else {
          router.replace(window.location.pathname);
        }
      }
    }
  }

  if (!isAuthenticated) {
    return null;
  }

  if (redirect) {
    router.push(redirect);
  }

  const rehypeNewTabOpts = { target: '_blank', rel: ['nofollow', 'nopener', 'noreferrer'] };

  return (
    <>
      <Head>
        <title>Bandicoot - App - Buy</title>
      </Head>
      <Container maxWidth='xl'>
        {isSelfUserReady && !isSelfUser && (
          <Typography variant='subtitle1' color='error'>
            Purchasing is disabled when acting as another user.
          </Typography>
        )}

        {isSelfUser && <>
          <AppBar position="static" className={classes.tabs}>
            <Tabs
              value={productType}
              onChange={(event, newValue) => { updateProductType(newValue); } }
              indicatorColor='secondary'
              textColor='secondary'
              variant='fullWidth'
              classes={{
                indicator: classes.indicator,
              }}
            >
              <Tab
                value={ProductTypes.PACKAGES}
                className={classes.tab}
                label='Scan Packages'
                icon={<PackageIcon />}
              />
              <Tab
                value={ProductTypes.CREDITS}
                className={classes.tab}
                label='Credit Packs'
                icon={<CreditIcon />}
              />
              <Tab
                value={ProductTypes.FRAMES}
                className={classes.tab}
                label='PRO Frames'
                icon={<ChartIcon />}
              />
            </Tabs>
          </AppBar>
          <TabPanel value={productType} index={ProductTypes.PACKAGES}>
            <stripe-pricing-table
              pricing-table-id={pricingTableIds[ProductTypes.PACKAGES]}
              publishable-key={process.env.NEXT_PUBLIC_STRIPE_KEY}
              client-reference-id={cleanUserId(user.sub)}
              customer-session-client-secret={clientSecret}
              >
            </stripe-pricing-table>
            <Container maxWidth='sm' style={{ paddingTop: '3em' }}>
              <Typography color='secondary' component='span'>
                <Markdown
                  components={{ img: FaqImage }}
                  rehypePlugins={[[rehypeExternalLinks, rehypeNewTabOpts]]}
                >
                  {pricingTableFaqs[ProductTypes.PACKAGES]}
                </Markdown>
              </Typography>
            </Container>
          </TabPanel>
          <TabPanel value={productType} index={ProductTypes.CREDITS}>
            <stripe-pricing-table
              pricing-table-id={pricingTableIds[ProductTypes.CREDITS]}
              publishable-key={process.env.NEXT_PUBLIC_STRIPE_KEY}
              client-reference-id={cleanUserId(user.sub)}
              customer-session-client-secret={clientSecret}
              >
            </stripe-pricing-table>
            <Container maxWidth='sm' style={{ paddingTop: '3em' }}>
              <Typography color='secondary' component='span'>
              <Markdown
                  components={{ img: FaqImage }}
                  rehypePlugins={[[rehypeExternalLinks, rehypeNewTabOpts]]}
                >
                  {pricingTableFaqs[ProductTypes.CREDITS]}
                </Markdown>
              </Typography>
            </Container>
          </TabPanel>
          <TabPanel value={productType} index={ProductTypes.FRAMES}>
            <stripe-pricing-table
              pricing-table-id={pricingTableIds[ProductTypes.FRAMES]}
              publishable-key={process.env.NEXT_PUBLIC_STRIPE_KEY}
              client-reference-id={cleanUserId(user.sub)}
              customer-session-client-secret={clientSecret}
            >
            </stripe-pricing-table>
            <Container maxWidth='sm' style={{ paddingTop: '3em' }}>
              <Typography color='secondary' component='span'>
              <Markdown
                  components={{ img: FaqImage }}
                  rehypePlugins={[[rehypeExternalLinks, rehypeNewTabOpts]]}
                >
                  {pricingTableFaqs[ProductTypes.FRAMES]}
                </Markdown>
              </Typography>
            </Container>
          </TabPanel>
        </>}
      </Container>
    </>
  );
}

export default withAuthenticationRequired(
  Buy,
  { onBeforeAuthentication: () => { logAuthenticationRequired('Buy') } }
);
